import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="py-20 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Overview</h2>
            <p>
              Data Care Solutions ("we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, and protect information when you visit our website at datacaresolutions.net.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.1 Contact Form Information</h3>
            <p>
              When you submit our contact form, we collect:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>Your name</li>
              <li>Your email address</li>
              <li>Any message or information you choose to provide</li>
            </ul>
            <p className="mt-4">
              This information is processed through Formspree, a third-party form handling service. We use this information solely to respond to your inquiries about our consulting services.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2 Server Logs</h3>
            <p>
              Like most websites, our web server automatically collects certain technical information when you visit our site, including:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
            </ul>
            <p className="mt-4">
              This information is stored in standard server logs and is used for technical troubleshooting, security purposes, and understanding general website usage patterns.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. What We Do NOT Collect</h2>
            <p className="font-semibold">
              We want to be clear about what we don't do:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>We do NOT use cookies for tracking or analytics purposes</li>
              <li>We do NOT use Google Analytics or similar analytics services</li>
              <li>We do NOT use advertising tracking pixels</li>
              <li>We do NOT use social media tracking tools</li>
              <li>We do NOT sell, rent, or share your personal information with third parties for marketing purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How We Use Your Information</h2>
            <p>
              We use the information we collect only for the following purposes:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>To respond to inquiries submitted through our contact form</li>
              <li>To maintain and improve the security of our website</li>
              <li>To troubleshoot technical issues</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Third-Party Services</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.1 Formspree</h3>
            <p>
              Our contact form is powered by Formspree. When you submit the form, your information is transmitted to and processed by Formspree before being forwarded to us. Formspree has its own privacy policy, which you can review at{' '}
              <a href="https://formspree.io/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                formspree.io/legal/privacy-policy
              </a>.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 External Links</h3>
            <p>
              Our website may contain links to external sites. We are not responsible for the privacy practices of other websites. We encourage you to read the privacy policies of any external sites you visit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p>
              We retain contact form submissions for as long as necessary to respond to your inquiry and maintain business records. Server logs are retained for a limited time for security and technical purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Security</h2>
            <p>
              We implement reasonable security measures to protect your information from unauthorized access, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Your Rights</h2>
            <p>
              You have the right to:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>Request access to any personal information we hold about you</li>
              <li>Request correction or deletion of your personal information</li>
              <li>Opt out of future communications</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at Admin@datacaresolutions.net.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p>
              Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last Updated" date at the top of this policy. Your continued use of our website after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-2">
              Email: <a href="mailto:Admin@datacaresolutions.net" className="text-blue-600 hover:text-blue-800">Admin@datacaresolutions.net</a>
            </p>
            <p className="mt-2">
              Data Care Solutions<br />
              Flagstaff, Arizona
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
